import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DepositData } from '../deposits.interface';
import { OptionItem } from '../../../shared/types/api-responses.type';
import { MasterDepositFormValidations } from './deposit-form-validations';
import { DepositsService } from '../../../services/deposits/deposits.service';
import { firstValueFrom } from 'rxjs';
import { showAlert } from '../../../shared/helpers/alert';
import { FileUploadWithPreview } from 'file-upload-with-preview';

@Component({
  selector: 'app-deposit-form',
  templateUrl: './deposit-form.component.html',
  styleUrls: ['./deposit-form.component.css'],
})
export class DepositFormComponent implements OnInit {
  loading: boolean = false;
  form!: FormGroup;
  readonly id: string;
  depositData!: DepositData;
  roles: OptionItem[] = [];
  readonly MasterDepositFormValidations = MasterDepositFormValidations;
  uploadInstance!: FileUploadWithPreview;
  imagePreviewUrl: string | null = null;

  statuses: OptionItem[] = [
    { label: 'Pending', value: '1' },
    { label: 'Failed', value: '2' },
    { label: 'Success', value: '0' },
  ];

  // Single Upload
  codeArr: any = [];

  constructor(
    private cdRef: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly depositService: DepositsService,
    private readonly router: Router
  ) {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
  }

  async ngOnInit(): Promise<void> {
    this.initForm();
    await this.loadInitialData();

    // single image upload start
    this.uploadInstance = new FileUploadWithPreview('myFirstImage', {
      images: {
        baseImage: '/assets/images/file-preview.svg',
        backgroundImage: '',
      },
      accept: 'image/*', // Optional: pastikan hanya image
    });

    const input = this.uploadInstance.inputHidden;
    input.addEventListener('change', () => {
      const file = this.uploadInstance.cachedFileArray?.[0];
      console.log('Dari cache:', file);

      if (!file) {
        console.warn('No file selected from cache');
        return;
      }

      if (!file.type.startsWith('image/')) {
        alert('Only images allowed');
        input.value = '';
        return;
      }

      this.form.get('file')?.setValue(file);
      this.form.get('file')?.markAsTouched();
      console.log('File set to form from cache:', file);
    });

    // single image upload end
  }

  private initForm(): void {
    this.form = this.fb.group({
      amount: [null, [Validators.required, Validators.min(0)]],
      file: [null, Validators.required],
      status: [null, Validators.required],
    });
    this.form.get('status')?.setValue('3');

    this.form.valueChanges.subscribe((value) => console.log('Form Value Changed:', value));
  }

  private async loadInitialData(): Promise<void> {
    this.loading = true;
    try {
      if (this.id) {
        await this.getDepositData();
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      this.showNotification('error', 'Error', 'Failed to load some initial data.');
    } finally {
      this.loading = false;
    }
  }

  private async getDepositData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.depositService.getDepositById(this.id));
      this.depositData = data;
      this.patchDepositData(data);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch deposit', JSON.stringify(error));
    }
  }

  private patchDepositData(deposit: DepositData): void {
    console.log('Patching Deposit Data:', deposit);
    console.log('Form Controls Before Patch:', this.form.controls);

    // Lakukan patch form values seperti biasa
    this.form.patchValue({
      id: deposit.id || '',
      tx_id: deposit.tx_id || '',
      bill_id: deposit.bill_id || '',
      ref_id: deposit.ref_id || '',
      amount: deposit.amount || 0,
      fee_merchant: deposit.fee_merchant || 0,
      fee_bank: deposit.fee_bank || 0,
      fee: deposit.fee || 0,
      fee_benefit: deposit.fee_benefit || 0,
      total_amount: deposit.total_amount || 0,
      total_admin: deposit.total_admin || 0,
      deducted_balance: deposit.deducted_balance || 0,
      type: deposit.type || '',
      // status: deposit.status || '',
      sign: deposit.sign || '',
      merchant_code: deposit.merchant_code || '',
      provider_code: deposit.provider_code || '',
      category_code: deposit.category_code || '',
      product_code: deposit.product_code || '',
      tx_detail: deposit.tx_detail || '',
      first_balance: deposit.first_balance || 0,
      last_balance: deposit.last_balance || 0,
      req_inq_param: deposit.req_inq_param || '',
      res_inq_param: deposit.res_inq_param || '',
      req_pay_param: deposit.req_pay_param || '',
      res_pay_param: deposit.res_pay_param || '',
      batch_time: deposit.batch_time || 0,
      active: deposit.active || '',
      va_no: deposit.va_no || '',
      va_bank_code: deposit.va_bank_code || '',
      bank_ref_id: deposit.bank_ref_id || '',
      is_settlement: deposit.is_settlement || 0,
      status_name: deposit.status_name || '',
      product_name: deposit.product_name || '',
      merchant_name: deposit.merchant_name || '',
      provider_name: deposit.provider_name || '',
      category_name: deposit.category_name || '',
      created_by: deposit.created_by || '',
      updated_by: deposit.updated_by || '',
      created_at: deposit.created_at || '',
      updated_at: deposit.updated_at || '',
    });

    const media = deposit.media;
    if (media?.media) {
      // this.imagePreviewUrl = `data:image/${media.file_type};base64,${media.media}`;
      this.imagePreviewUrl = media.media;
      this.form.get('file')?.clearValidators(); // hilangkan semua validator
      this.form.get('file')?.updateValueAndValidity();
    }
    console.log('this.imagePreviewUrl :' + this.imagePreviewUrl);

    // set status 0
    this.form.get('status')?.setValue(null);

    console.log('Form Value After Patch:', this.form.value);
  }

  async saveData(): Promise<void> {
    console.log('Form value before saving:', this.form.value);

    if (this.id) {
      await this.updateDeposit();
      return;
    }
    await this.createDeposit();
  }

  async createDeposit(): Promise<void> {
    try {
      console.log('Form value before saving:', this.form.value);

      const resp = await firstValueFrom(this.depositService.createDeposit(this.form.value));

      if (resp.code === 0) {
        this.showNotification('success', 'Success', 'Deposit created successfully');
        await this.router.navigate(['/deposits/list']);
      } else {
        this.showNotification('error', 'Error', 'Failed to create deposit');
      }
    } catch (error) {
      this.showNotification('error', 'Error', JSON.stringify(error));
    }
  }

  async updateDeposit(): Promise<void> {
    try {
      console.log('Form value before saving:', this.form.value);

      const resp = await firstValueFrom(this.depositService.updateDeposit(this.id, this.form.value));

      if (resp.code === 0) {
        this.showNotification('success', 'Success', 'Deposit updated successfully');
        await this.router.navigate(['/deposits/list']);
      } else {
        this.showNotification('error', 'Error', 'Failed to update deposit');
      }
    } catch (error: any) {
      console.log('Error:', error);
      this.showNotification('error', 'Error', error);
    }
  }

  toggleCode = (name: string) => {
    if (this.codeArr.includes(name)) {
      this.codeArr = this.codeArr.filter((d: string) => d != name);
    } else {
      this.codeArr.push(name);
    }
  };

  onImageSelected(event: Event): void {
    console.log('File input changed!'); // Log ketika event change dipicu

    const inputEl = document.getElementById('file-upload-with-preview-myFirstImage') as HTMLInputElement;
    console.log('Selected via inputEl:', inputEl);
    const file = inputEl?.files?.[0];

    if (file) {
      console.log('Selected via ID:', file);
      this.form.get('file')?.setValue(file);
    }

    // const input = event.target as HTMLInputElement;
    // if (!input.files || input.files.length === 0) {
    //   console.log('No file selected');
    //   return;
    // }
    //
    // const file = input.files[0];

    console.log('Selected file:', file); // File pertama yang dipilih

    // Validasi file jika bukan image
    // if (!file.type.startsWith('image/')) {
    //   alert('Only image files are allowed!');
    //   //target.value = ''; // Reset input file
    //   this.form.get('file')?.setValue(null); // Reset nilai form control
    //   return;
    // }

    // Set file ke FormControl
    this.form.get('file')?.setValue(file);
    this.form.get('file')?.markAsTouched();
    console.log('File set in form control:', this.form.get('file')?.value);
  }

  private showNotification(type: 'success' | 'error', title: string, text = 'Success'): void {
    showAlert({ type, title, text }).then();
  }
}
